import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const LAST_UPDATED = "15/04/2026";

const LinkButton = ({ children }) => (
  <span className="font-inter text-sm text-cream-400 underline underline-offset-4 transition-colors duration-200 hover:text-gold">
    {children}
  </span>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-poppins text-base font-semibold text-navy-900 md:text-lg">
    {children}
  </h3>
);

const SectionText = ({ children }) => (
  <p className="font-inter text-sm leading-relaxed text-navy-700 md:text-[15px]">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 pl-5 font-inter text-sm leading-relaxed text-navy-700 md:text-[15px] list-disc">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const DocumentDialog = ({ triggerLabel, title, description, children }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button type="button" className="bg-transparent">
        <LinkButton>{triggerLabel}</LinkButton>
      </button>
    </DialogTrigger>
    <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden rounded-2xl border-0 bg-cream-300 p-0">
      <div className="max-h-[85vh] overflow-y-auto p-6 md:p-8">
        <DialogHeader className="border-b border-navy-900/10 pb-5 pr-8">
          <DialogTitle className="font-poppins text-2xl text-navy-900">
            {title}
          </DialogTitle>
          <DialogDescription className="font-inter text-sm text-navy-600">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-6 space-y-5">{children}</div>
      </div>
    </DialogContent>
  </Dialog>
);

export const LegalDocuments = () => {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
      <DocumentDialog
        triggerLabel="Aviso legal"
        title="Aviso legal"
        description={`Última actualización: ${LAST_UPDATED}`}
      >
        <SectionTitle>1. Titular del sitio</SectionTitle>
        <SectionText>
          Este sitio web corresponde a Bárbara Cargnello, contadora pública
          matriculada, con presencia profesional en Berazategui, Buenos Aires,
          y atención online para Buenos Aires y CABA.
        </SectionText>

        <SectionTitle>2. Objeto del sitio</SectionTitle>
        <SectionText>
          El sitio tiene fines informativos y de contacto comercial. Su
          contenido describe los servicios profesionales ofrecidos, los canales
          de contacto disponibles y datos generales de actividad.
        </SectionText>

        <SectionTitle>3. Alcance de la información publicada</SectionTitle>
        <SectionText>
          La información publicada en este sitio es general y orientativa. No
          constituye por sí sola asesoramiento profesional personalizado, ni
          reemplaza una consulta contable, impositiva o laboral adaptada a cada
          caso concreto.
        </SectionText>

        <SectionTitle>4. Uso del sitio</SectionTitle>
        <BulletList
          items={[
            "La persona usuaria se compromete a utilizar este sitio de manera lícita y de buena fe.",
            "No debe realizar acciones que afecten la disponibilidad, seguridad o funcionamiento del sitio.",
            "No debe utilizar los contenidos para fines ilícitos, engañosos o que puedan perjudicar a terceros.",
          ]}
        />

        <SectionTitle>5. Propiedad intelectual</SectionTitle>
        <SectionText>
          Los textos, imágenes, diseños, marcas, logotipos y demás contenidos
          del sitio pertenecen a su titular o se utilizan con autorización. No
          está permitida su reproducción, distribución, modificación o uso
          comercial sin autorización previa y por escrito.
        </SectionText>

        <SectionTitle>6. Enlaces externos</SectionTitle>
        <SectionText>
          El sitio puede incluir enlaces a servicios de terceros, como WhatsApp,
          Instagram o TikTok. El acceso a esos servicios se rige por las
          condiciones y políticas de privacidad propias de cada plataforma.
        </SectionText>

        <SectionTitle>7. Limitación de responsabilidad</SectionTitle>
        <BulletList
          items={[
            "Se procura que la información del sitio sea clara y esté actualizada, pero no se garantiza la ausencia total de errores u omisiones.",
            "La titular no será responsable por daños derivados del uso del sitio, de interrupciones técnicas o de decisiones tomadas exclusivamente en base al contenido general aquí publicado.",
            "La contratación de servicios profesionales queda sujeta a evaluación previa, aceptación del caso y eventual acuerdo particular con cada cliente.",
          ]}
        />

        <SectionTitle>8. Modificaciones</SectionTitle>
        <SectionText>
          Este aviso legal puede ser actualizado para reflejar cambios en el
          sitio, en los servicios o en la normativa aplicable. La versión
          vigente será siempre la publicada en este mismo apartado.
        </SectionText>

        <SectionTitle>9. Contacto</SectionTitle>
        <SectionText>
          Para consultas sobre este aviso legal o sobre el uso del sitio, podés
          comunicarte por WhatsApp al +54 9 11 6516-2564 o por los canales
          oficiales publicados en esta web.
        </SectionText>
      </DocumentDialog>

      <DocumentDialog
        triggerLabel="Política de privacidad"
        title="Política de privacidad"
        description={`Última actualización: ${LAST_UPDATED}`}
      >
        <SectionTitle>1. Responsable del tratamiento</SectionTitle>
        <SectionText>
          La responsable del tratamiento de los datos personales recabados a
          través de este sitio es Bárbara Cargnello, contadora pública
          matriculada.
        </SectionText>

        <SectionTitle>2. Datos que pueden recopilarse</SectionTitle>
        <BulletList
          items={[
            "Datos que compartís voluntariamente al iniciar contacto por WhatsApp u otros canales vinculados desde el sitio, como nombre, teléfono y el contenido de tu consulta.",
            "Datos técnicos y de navegación, como dirección IP, dispositivo, navegador, páginas visitadas e interacciones con el sitio.",
            "Información estadística y de uso obtenida mediante herramientas de analítica para mejorar el rendimiento y la experiencia de navegación.",
          ]}
        />

        <SectionTitle>3. Finalidades del tratamiento</SectionTitle>
        <BulletList
          items={[
            "Responder consultas y solicitudes comerciales o profesionales.",
            "Gestionar potenciales contrataciones y mantener comunicaciones vinculadas a los servicios ofrecidos.",
            "Medir el uso del sitio, detectar errores, mejorar contenidos y optimizar la experiencia de las personas usuarias.",
            "Cumplir obligaciones legales o requerimientos de autoridad competente, cuando corresponda.",
          ]}
        />

        <SectionTitle>4. Base de legitimación</SectionTitle>
        <SectionText>
          El tratamiento se realiza sobre la base del consentimiento brindado al
          contactar voluntariamente por los canales del sitio y del interés
          legítimo de mantener la seguridad, funcionamiento y mejora del
          servicio digital.
        </SectionText>

        <SectionTitle>5. Analítica, cookies y tecnologías similares</SectionTitle>
        <SectionText>
          Este sitio utiliza herramientas de analítica para conocer de forma
          agregada como se navega y detectar mejoras. Actualmente se emplea
          PostHog para medición de uso y rendimiento. Estas tecnologías pueden
          apoyarse en cookies u otros identificadores técnicos del navegador.
        </SectionText>

        <SectionTitle>6. Conservación de los datos</SectionTitle>
        <SectionText>
          Los datos se conservarán durante el tiempo necesario para atender tu
          consulta, mantener la relación profesional o comercial, cumplir
          obligaciones legales y resguardar eventuales responsabilidades.
        </SectionText>

        <SectionTitle>7. Comunicación a terceros</SectionTitle>
        <SectionText>
          Los datos no se venden ni se ceden con fines comerciales ajenos a la
          actividad profesional. Pueden ser tratados por proveedores tecnologicos
          que intervienen en la operación del sitio o por obligaciones legales,
          siempre dentro de lo necesario para esas finalidades.
        </SectionText>

        <SectionTitle>8. Derechos de las personas titulares</SectionTitle>
        <SectionText>
          Conforme al artículo 43 de la Constitución Nacional, la Ley 25.326 y
          su reglamentación, podés ejercer los derechos de información, acceso,
          rectificación, actualización y supresión de tus datos personales.
        </SectionText>
        <BulletList
          items={[
            "Las solicitudes de acceso deben ser respondidas dentro de los 10 días corridos.",
            "Las solicitudes de rectificación, actualización o supresión deben ser atendidas dentro de los 5 días hábiles.",
            "Si la respuesta fuera insuficiente o no existiera respuesta, podés acudir ante la Agencia de Acceso a la Información Pública (AAIP), autoridad de control en materia de protección de datos personales en Argentina.",
          ]}
        />

        <SectionTitle>9. Cómo ejercer tus derechos</SectionTitle>
        <SectionText>
          Podés solicitar el ejercicio de tus derechos escribiendo al canal de
          contacto publicado en este sitio, indicando tu nombre, medio de
          contacto y el pedido que deseas realizar respecto de tus datos.
        </SectionText>

        <SectionTitle>10. Seguridad</SectionTitle>
        <SectionText>
          Se adoptan medidas razonables de gestión y resguardo para proteger la
          información tratada. Sin embargo, ningún sistema es completamente
          invulnerable y no puede garantizarse seguridad absoluta.
        </SectionText>

        <SectionTitle>11. Cambios en esta política</SectionTitle>
        <SectionText>
          Esta política puede actualizarse para reflejar cambios operativos,
          legales o técnicos. Toda modificación regirá desde su publicación en
          este sitio.
        </SectionText>
      </DocumentDialog>
    </div>
  );
};

